import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '@ohif/ui-next';

interface FormTimePickerProps {
  label: string;
  required?: boolean;
  value?: string; // Format: "HH:mm"
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

const FormTimePicker: React.FC<FormTimePickerProps> = ({
  label,
  required = false,
  value = '',
  onChange,
  placeholder = 'Select time',
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState<string>('');
  const [minutes, setMinutes] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse initial value
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h || '');
      setMinutes(m || '');
    } else {
      setHours('');
      setMinutes('');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleHourSelect = (hour: string) => {
    setHours(hour);
    if (minutes) {
      onChange?.(`${hour}:${minutes}`);
    }
  };

  const handleMinuteSelect = (minute: string) => {
    setMinutes(minute);
    if (hours) {
      onChange?.(`${hours}:${minute}`);
    }
  };

  const displayValue = value || (hours && minutes ? `${hours}:${minutes}` : '');

  // Generate hour options (00-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));

  // Generate minute options (00-59, step of 5 for better UX)
  const minuteOptions = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          position: 'relative',
        }}
      >
        <label
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: disabled ? 'var(--label-color-disabled)' : 'var(--label-color-default)',
            lineHeight: '20px',
          }}
        >
          {label}
        </label>
        {required && (
          <span
            style={{
              color: '#dc2626',
              fontSize: '10px',
              position: 'absolute',
              left: '-8px',
              top: '2px',
              fontWeight: 600,
            }}
          >
            *
          </span>
        )}
      </div>

      {/* Input Field */}
      <div
        ref={containerRef}
        style={{ position: 'relative', width: '100%' }}
      >
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={{
            width: '100%',
            height: '36px',
            padding: '8px 12px',
            backgroundColor: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input-default)',
            border: `1px solid ${error ? '#dc2626' : disabled ? 'var(--border-input-disabled)' : 'var(--border-input-default)'}`,
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 400,
            color: disabled
              ? 'var(--text-input-disabled)'
              : displayValue
                ? 'var(--text-input-default)'
                : '#64748b',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: 'inset 0px 2px 4px 0px rgba(0,0,0,0.06)',
          }}
        >
          <span>{displayValue || placeholder}</span>
          <Icons.DownArrowIcon
            width="16"
            height="16"
            style={{
              color: '#94A3B8',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: '#0f172a',
              border: '1px solid #1e293b',
              borderRadius: '6px',
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
              display: 'flex',
              gap: '8px',
              padding: '8px',
              minWidth: '200px',
            }}
          >
            {/* Hours Column */}
            <div
              style={{
                flex: 1,
                maxHeight: '200px',
                overflowY: 'auto',
                borderRight: '1px solid #1e293b',
                paddingRight: '8px',
              }}
            >
              {hourOptions.map(hour => (
                <div
                  key={hour}
                  onClick={() => handleHourSelect(hour)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: hours === hour ? '#1e293b' : 'transparent',
                    color: hours === hour ? 'var(--accent-primary)' : '#e2e8f0',
                    fontSize: '14px',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}
                  onMouseEnter={e => {
                    if (hours !== hour) {
                      e.currentTarget.style.backgroundColor = '#1e293b';
                    }
                  }}
                  onMouseLeave={e => {
                    if (hours !== hour) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {hour}
                </div>
              ))}
            </div>

            {/* Minutes Column */}
            <div
              style={{
                flex: 1,
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {minuteOptions.map(minute => (
                <div
                  key={minute}
                  onClick={() => handleMinuteSelect(minute)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: minutes === minute ? '#1e293b' : 'transparent',
                    color: minutes === minute ? 'var(--accent-primary)' : '#e2e8f0',
                    fontSize: '14px',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}
                  onMouseEnter={e => {
                    if (minutes !== minute) {
                      e.currentTarget.style.backgroundColor = '#1e293b';
                    }
                  }}
                  onMouseLeave={e => {
                    if (minutes !== minute) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {minute}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <span
          style={{
            fontSize: '12px',
            color: '#dc2626',
            marginTop: '4px',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default FormTimePicker;
